import React, { useState, useEffect, useRef } from 'react';
import FileIcon from '../icons/FileIcon';
import JsonIcon from '../icons/JsonIcon';
import CsvIcon from '../icons/CsvIcon';
import ExcelIcon from '../icons/ExcelIcon';
import PdfIcon from '../icons/PdfIcon';
import PptIcon from '../icons/PptIcon';
import DoubleCheckIcon from '../icons/DoubleCheckIcon';
import SendMessageInput from './SendMessageInput';
import SpinningLoader from './SpinningLoader';
import { ChatMessage } from '../../types/chat';
import { StreamingQueryResponse, QueryStep } from '../../services/streamingApi';
import './ChatPanel.css';

interface ChatPanelProps {
  messages: ChatMessage[];
  onSendMessage: (message: string, attachments: File[], metadata?: any) => void;
  onFileSelect: () => void;
  onFileRemove: (index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  isDragOver: boolean;
  attachedFiles: File[];
  className?: string;
  isStreaming?: boolean;
  isChatLoading?: boolean;
  streamingResponse?: StreamingQueryResponse;
  showWorkspace?: boolean;
  isFollowUpQuery?: boolean;
  projectName?: string;
  isUploadingFiles?: boolean;
  uploadingMessageId?: string | null;
  uploadProgress?: number;
  hasUserApproved?: boolean;
  isAgentMode?: boolean;
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'json':
      return JsonIcon;
    case 'csv':
      return CsvIcon;
    case 'xls':
    case 'xlsx':
      return ExcelIcon;
    case 'pdf':
      return PdfIcon;
    case 'ppt':
    case 'pptx':
      return PptIcon;
    default:
      return FileIcon;
  }
};

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  onSendMessage,
  onFileSelect,
  onFileRemove,
  onDragOver,
  onDragLeave,
  onDrop,
  onPaste,
  isDragOver,
  attachedFiles,
  className,
  isStreaming = false,
  isChatLoading = false,
  streamingResponse,
  showWorkspace,
  isFollowUpQuery = false,
  projectName,
  isUploadingFiles = false,
  uploadingMessageId = null,
  uploadProgress = 0,
  hasUserApproved = false,
  isAgentMode = true
}) => {
  const [currentMessage, setCurrentMessage] = useState('');
  const [isOrchestrationExpanded, setIsOrchestrationExpanded] = useState(true);
  const [hasAddedCompletionMessage, setHasAddedCompletionMessage] = useState(false);
  const [isProceedClicked, setIsProceedClicked] = useState(false);

  const [completedAgents, setCompletedAgents] = useState<Set<string>>(new Set());
  const [delayedCurrentAgent, setDelayedCurrentAgent] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  // Debug: Log when streamingResponse changes
  useEffect(() => {
    console.log('[ChatPanel] streamingResponse changed:', {
      isStreaming,
      isChatLoading,
      streamingResponse,
      hasStreamingResponse: !!streamingResponse
    });
  }, [streamingResponse, isStreaming, isChatLoading]);

  const transformAgentName = (name: string): string => {
    // If the name already contains "Agent", don't transform it
    if (name.toLowerCase().includes('agent')) {
      return name;
    }

    // Replace "analyst" with "Agent" while preserving case
    return name.replace(/analyst/gi, (match) => {
      // Check if first letter is uppercase
      if (match[0] === match[0].toUpperCase()) {
        return 'Agent';
      }
      return 'agent';
    });
  };

  // Helper function to format message content with proper line breaks and markdown
  const formatMessageContent = (content: string | undefined): JSX.Element => {
    // Handle undefined or null content
    if (!content) {
      return <></>;
    }

    // Split by newlines
    const lines = content.split('\n');

    return (
      <>
        {lines.map((line, index) => {
          // Check if line is a bold header (starts and ends with **)
          if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
            const headerText = line.trim().slice(2, -2);
            return (
              <p key={index} style={{ fontWeight: 'bold', marginTop: index > 0 ? '1em' : 0, marginBottom: '0.5em' }}>
                {headerText}
              </p>
            );
          }

          // Check if line is a bullet point (starts with •)
          if (line.trim().startsWith('•')) {
            return (
              <p key={index} style={{ marginLeft: '1em' }}>
                {line.trim()}
              </p>
            );
          }

          // Regular line
          if (line.trim()) {
            return <p key={index}>{line}</p>;
          }

          // Empty line (for spacing)
          return <div key={index} style={{ height: '0.5em' }} />;
        })}
      </>
    );
  };

  // Sort all messages by timestamp to ensure proper chronological order
  const sortedMessages = React.useMemo(() => {
    const messagesCopy = [...messages];
    messagesCopy.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB;
    });
    return messagesCopy;
  }, [messages]);

  // Track if user has manually scrolled up during current streaming session
  const userHasScrolledUpRef = useRef<boolean>(false);

  // Check if user is near the bottom of the scroll container
  const isNearBottom = () => {
    if (!chatMessagesRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
    // Consider "near bottom" if within 150px of the bottom
    return scrollHeight - scrollTop - clientHeight < 150;
  };

  // Handle user scroll to detect when they manually scroll up
  const handleScroll = () => {
    if (!chatMessagesRef.current) return;

    if (isNearBottom()) {
      // User scrolled back to bottom, allow auto-scroll again
      userHasScrolledUpRef.current = false;
    } else {
      // User scrolled up, disable auto-scroll
      userHasScrolledUpRef.current = true;
    }
  };

  const scrollToBottom = (smooth: boolean = true, force: boolean = false) => {
    // If user has manually scrolled up, don't auto-scroll unless force is true
    if (!force && userHasScrolledUpRef.current) {
      return;
    }

    // Multiple approaches to ensure reliable scrolling
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }

    // Also try scrolling the messages container directly
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when messages change (respect user scroll position)
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Scroll to bottom immediately when component mounts (page load) - force scroll
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      scrollToBottom(false, true); // No smooth animation on initial load, force scroll
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Force scroll when orchestration content first appears (agentStatuses becomes populated)
  const prevAgentStatusesLengthRef = useRef<number>(0);
  useEffect(() => {
    const currentLength = streamingResponse?.agentStatuses?.length || 0;
    const prevLength = prevAgentStatusesLengthRef.current;
    prevAgentStatusesLengthRef.current = currentLength;

    // When agentStatuses goes from 0 to >0, force scroll to show orchestration panel
    if (prevLength === 0 && currentLength > 0) {
      userHasScrolledUpRef.current = false; // Reset so we scroll
      const timer = setTimeout(() => {
        scrollToBottom(true, true); // Smooth animation, force scroll
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [streamingResponse?.agentStatuses?.length]);

  // Scroll to bottom when streaming response updates (respect user scroll position)
  // Don't auto-scroll during pending_approval to allow users to review execution plan
  useEffect(() => {
    if (streamingResponse && streamingResponse.status !== 'pending_approval') {
      // Small delay to ensure DOM updates are complete
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [streamingResponse, streamingResponse?.steps]);



  // Reset proceed button state when streaming status changes
  useEffect(() => {
    if (streamingResponse?.status === 'pending_approval') {
      setIsProceedClicked(false);
    }
    else {
      setIsProceedClicked(true);
    }
  }, [streamingResponse?.status]);

  // Track when to show completion message (only for non-follow-up queries)
  useEffect(() => {
    if (!isStreaming &&
      streamingResponse?.status === 'completed' &&
      !hasAddedCompletionMessage &&
      !isFollowUpQuery) {

      // Small delay to ensure orchestration is fully rendered
      const timer = setTimeout(() => {
        setHasAddedCompletionMessage(true);
        // Scroll to bottom when completion message is added
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isStreaming, streamingResponse?.status, hasAddedCompletionMessage, isFollowUpQuery]);

  // Reset completion message flag when new query starts
  useEffect(() => {
    if (isStreaming) {
      setHasAddedCompletionMessage(false);
    }
  }, [isStreaming]);

  // Scroll to bottom when workspace is shown/hidden
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100); // Slightly longer delay for layout changes

    return () => clearTimeout(timer);
  }, [showWorkspace]);

  // Extract execution plan agents from messages or streamingResponse
  const executionPlanAgents = React.useMemo<string[]>(() => {
    // Find the most recent execution plan message
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].metadata?.type === 'execution_plan') {
        const executionPlan = messages[i].metadata?.executionPlan;
        if (executionPlan?.agents && Array.isArray(executionPlan.agents)) {
          // Handle both string[] and object[] formats
          return executionPlan.agents.map((agent: any) => {
            if (typeof agent === 'string') {
              return transformAgentName(agent);
            } else if (agent && typeof agent === 'object' && agent.agentName) {
              // .NET backend format: { agentId, agentName, description, order }
              return transformAgentName(agent.agentName);
            }
            return 'Unknown Agent';
          });
        }
        break;
      }
    }

    // check streaming response as fallback
    if (streamingResponse?.executionPlan?.agents && Array.isArray(streamingResponse.executionPlan.agents)) {
      // Handle both string[] and object[] formats
      return streamingResponse.executionPlan.agents.map((agent: any) => {
        if (typeof agent === 'string') {
          return transformAgentName(agent);
        } else if (agent && typeof agent === 'object' && agent.agentName) {
          // .NET backend format: { agentId, agentName, description, order }
          return transformAgentName(agent.agentName);
        }
        return 'Unknown Agent';
      });
    }

    return [];
  }, [messages, streamingResponse?.executionPlan]);

  // Reset completed agents when a new execution plan is shown
  React.useEffect(() => {
    // Find if there's a new execution plan message
    const hasExecutionPlan = messages.some(msg => msg.metadata?.type === 'execution_plan');
    if (hasExecutionPlan && completedAgents.size > 0 && !isStreaming) {
      // Reset when we have an execution plan but not streaming yet
      setCompletedAgents(new Set());
      setDelayedCurrentAgent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only re-runs when the message count changes; adding messages/completedAgents/isStreaming would reset agent progress mid-stream
  }, [messages.length]); // Only trigger when message count changes

  // Delay the current agent transition by 2 seconds
  React.useEffect(() => {
    if (!streamingResponse || !isStreaming) {
      setDelayedCurrentAgent(null);
      return;
    }

    const currentAgentName = streamingResponse?.currentAgent || streamingResponse?.activeAgent;
    if (currentAgentName) {
      const transformedAgentName = transformAgentName(currentAgentName);
      const currentAgentKey = transformedAgentName.toLowerCase().trim().replace(/\s+/g, ' ');

      // Only update if it's different from the delayed current agent
      if (currentAgentKey !== delayedCurrentAgent) {
        const timer = setTimeout(() => {
          // When transitioning to a new agent, mark the previous one as completed
          if (delayedCurrentAgent && !delayedCurrentAgent.includes('strategy')) {
            setCompletedAgents(prev => {
              if (!prev.has(delayedCurrentAgent)) {
                const updated = new Set(prev);
                updated.add(delayedCurrentAgent);
                return updated;
              }
              return prev;
            });
          }

          setDelayedCurrentAgent(currentAgentKey);
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adding the whole streamingResponse would clear and re-arm the 2s transition timer on every streaming chunk; only the agent fields matter here
  }, [streamingResponse?.currentAgent, streamingResponse?.activeAgent, isStreaming, delayedCurrentAgent]);

  // Update completed agents when we detect new completions or based on current agent position
  React.useEffect(() => {
    // Skip if not streaming
    if (!isStreaming && !streamingResponse) return;

    const newCompletedAgents = new Set(completedAgents);

    // Add completed agents from steps with 2 second delay
    if (streamingResponse?.steps && Array.isArray(streamingResponse.steps)) {
      streamingResponse.steps.forEach((step: any) => {
        if (step.status === 'completed') {
          const agentKey = transformAgentName(step.agent).toLowerCase().trim().replace(/\s+/g, ' ');
          if (!completedAgents.has(agentKey) && !newCompletedAgents.has(agentKey)) {
            // Add with 2 second delay
            setTimeout(() => {
              setCompletedAgents(prev => {
                // Check again if not already completed (could have been added during the delay)
                if (!prev.has(agentKey)) {
                  const updated = new Set(prev);
                  updated.add(agentKey);
                  return updated;
                }
                return prev; // No change if already completed
              });
            }, 2000);
          }
        }
      });
    }

    // Add inferred completed agents based on current agent position
    if (streamingResponse?.status === 'processing') {
      // Use currentAgent or activeAgent from the response
      const currentAgentName = streamingResponse?.currentAgent || streamingResponse?.activeAgent;
      if (currentAgentName) {
        const transformedAgentName = transformAgentName(currentAgentName);
        const currentAgentKey = transformedAgentName.toLowerCase().trim().replace(/\s+/g, ' ');

        // For non-Strategy agents, find their position in the plan
        if (!currentAgentKey.includes('strategy')) {
          const currentAgentIndex = executionPlanAgents.findIndex(agent =>
            agent.toLowerCase().trim().replace(/\s+/g, ' ') === currentAgentKey
          );

          if (currentAgentIndex > 0) {
            // Mark all agents before the current one as completed with 2 second delay
            executionPlanAgents.slice(0, currentAgentIndex).forEach(agent => {
              const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
              if (!completedAgents.has(agentKey) && !newCompletedAgents.has(agentKey)) {
                // Add with 2 second delay
                setTimeout(() => {
                  setCompletedAgents(prev => {
                    // Check again if not already completed (could have been added during the delay)
                    if (!prev.has(agentKey)) {
                      const updated = new Set(prev);
                      updated.add(agentKey);
                      return updated;
                    }
                    return prev; // No change if already completed
                  });
                }, 2000);
              }
            });
          }
        }
        // Note: For Strategy Agent, we don't make assumptions about other agents being completed
        // since it runs both before (planning) and after (synthesis)
      }
    }

    // Note: State updates now happen via setTimeout, not immediately
    // eslint-disable-next-line react-hooks/exhaustive-deps -- completedAgents is updated by this effect via setTimeout; depending on it (or the whole streamingResponse) would re-schedule completion timers on every update
  }, [streamingResponse?.steps, streamingResponse?.status, streamingResponse?.currentAgent, streamingResponse?.activeAgent, executionPlanAgents, isStreaming]);

  // Prepopulate orchestration steps from executionPlan.agents
  const orchestrationSteps = React.useMemo<QueryStep[]>(() => {
    const steps: QueryStep[] = [];
    const agentStatusMap = new Map<string, string>();

    // First, initialize all agents from execution plan as pending
    executionPlanAgents.forEach(agent => {
      const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
      // Check if this agent is already completed (from persistent state)
      if (completedAgents.has(agentKey)) {
        agentStatusMap.set(agentKey, 'completed');
      } else {
        agentStatusMap.set(agentKey, 'pending');
      }
    });

    // Process steps from streaming response - these are COMPLETED agents only
    if (streamingResponse?.steps && Array.isArray(streamingResponse.steps)) {
      streamingResponse.steps.forEach((step: any) => {
        const agentName = transformAgentName(step.agent);
        // Normalize agent key - remove extra spaces and convert to lowercase
        const agentKey = agentName.toLowerCase().trim().replace(/\s+/g, ' ');

        // Skip Strategy Agent as it's handled separately as the final step
        if (agentKey.includes('strategy') && !step?.result?.executive_summary) {
          return;
        }


        // Steps array only contains completed agents
        agentStatusMap.set(agentKey, 'completed');
      });
    }

    // WORKAROUND: Infer completed agents based on the order of agents and current agent
    // Special handling based on streaming status
    if (streamingResponse?.status === 'completed') {
      // When completed, mark all agents as completed unless they were explicitly marked as failed/skipped/completed
      executionPlanAgents.forEach(agent => {
        const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
        const currentStatus = agentStatusMap.get(agentKey);
        if (currentStatus !== 'failed' && currentStatus !== 'skipped' && currentStatus !== 'completed') {
          agentStatusMap.set(agentKey, 'completed');
        }
      });
    } else if (streamingResponse?.status === 'processing') {
      // Use the delayed current agent instead of the actual current agent
      const currentAgentKey = delayedCurrentAgent;

      if (currentAgentKey) {
        // Find the index of current agent in the execution plan (if it's in the plan)
        const currentAgentIndex = executionPlanAgents.findIndex(agent =>
          agent.toLowerCase().trim().replace(/\s+/g, ' ') === currentAgentKey
        );


        if (currentAgentIndex > -1) {
          // Current agent is in the execution plan
          // Mark all agents before current agent as completed (using persistent state)
          executionPlanAgents.slice(0, currentAgentIndex).forEach(agent => {
            const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
            // Mark as completed (persistent state handles this in useEffect)
            agentStatusMap.set(agentKey, 'completed');
          });

          // Ensure current agent is marked as processing (unless already completed)
          if (!completedAgents.has(currentAgentKey)) {
            agentStatusMap.set(currentAgentKey, 'processing');
          } else {
            agentStatusMap.set(currentAgentKey, 'completed');
          }

          // Mark all agents after current as pending (unless failed/skipped/completed)
          executionPlanAgents.slice(currentAgentIndex + 1).forEach(agent => {
            const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
            if (!completedAgents.has(agentKey)) {
              const currentStatus = agentStatusMap.get(agentKey);
              if (currentStatus !== 'failed' && currentStatus !== 'skipped') {
                agentStatusMap.set(agentKey, 'pending');
              }
            } else {
              agentStatusMap.set(agentKey, 'completed');
            }
          });
        } else if (currentAgentKey.includes('strategy')) {
          // Strategy Agent is not in the execution plan but is processing
          // This could be either the initial planning phase or final synthesis
          // Check if all execution plan agents are completed in the steps array
          const completedAgentKeys = new Set<string>();
          if (streamingResponse?.steps) {
            streamingResponse.steps.forEach((step: any) => {
              const agentKey = step.agent.toLowerCase().trim().replace(/\s+/g, ' ');
              completedAgentKeys.add(agentKey);
            });
          }

          // Check if all execution plan agents are completed
          const allExecutionAgentsCompleted = executionPlanAgents.every(agent => {
            const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
            return completedAgentKeys.has(agentKey) || completedAgents.has(agentKey);
          });

          if (allExecutionAgentsCompleted) {
            // This is the final synthesis phase - all agents should be marked as completed

            // Add 2 second delay before marking all as completed
            setTimeout(() => {
              setCompletedAgents(prev => {
                const updated = new Set(prev);
                let addedAny = false;
                executionPlanAgents.forEach(agent => {
                  const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
                  // Check if not already completed before adding
                  if (!updated.has(agentKey)) {
                    updated.add(agentKey);
                    addedAny = true;
                  }
                });
                if (addedAny) {
                }
                return updated;
              });
            }, 2000);

            // Still set them in the map for immediate display logic
            executionPlanAgents.forEach(agent => {
              const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');
              agentStatusMap.set(agentKey, 'completed');
            });
          } else {
            // This is the planning phase - don't mark agents as completed yet
          }
        }
      }
    }


    // Build display steps - one per unique agent
    const uniqueAgents = new Set<string>();

    // Add agents from execution plan first
    if (executionPlanAgents.length > 0) {
      executionPlanAgents.forEach((agent: string) => {
        uniqueAgents.add(agent);
      });
    }

    // Add any agents from streaming that aren't in the plan
    if (streamingResponse?.steps) {
      streamingResponse.steps.forEach((step: any) => {
        const agentName = transformAgentName(step.agent);
        const agentKey = agentName.toLowerCase().trim().replace(/\s+/g, ' ');

        // Skip Strategy Agent that are not the last one as it's handled separately at the end
        if (agentKey.includes('strategy') && !step?.result?.executive_summary) {
          return;
        }

        // Check if this agent is already in our list (case insensitive)
        const exists = Array.from(uniqueAgents).some(a =>
          a.toLowerCase() === agentName.toLowerCase()
        );
        if (!exists) {
          uniqueAgents.add(agentName);
        }
      });
    }

    // Create steps for each unique agent
    Array.from(uniqueAgents).forEach((agent, index) => {
      // Normalize agent key the same way we did for the map
      const agentKey = agent.toLowerCase().trim().replace(/\s+/g, ' ');

      // Get status from our map
      let status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' =
        (agentStatusMap.get(agentKey) as any) || 'pending';


      let action = '';

      // Check if this agent is currently active (override status if so)
      // Use currentAgent/activeAgent from response
      const normalizedCurrentAgent = streamingResponse?.currentAgent ? transformAgentName(streamingResponse.currentAgent).toLowerCase().trim().replace(/\s+/g, ' ') : undefined;
      const normalizedActiveAgent = streamingResponse?.activeAgent ? transformAgentName(streamingResponse.activeAgent).toLowerCase().trim().replace(/\s+/g, ' ') : undefined;

      const isCurrentAgent = (normalizedCurrentAgent === agentKey ||
        normalizedActiveAgent === agentKey) &&
        streamingResponse?.status === 'processing';

      if (isCurrentAgent) {
        status = 'processing';
        action = streamingResponse.currentStep || 'Gathering insights...';
      } else if (status === 'completed') {
        action = `Analysis complete`;
      } else if (status === 'processing') {
        action = 'Processing...';
      } else if (status === 'failed') {
        action = 'Failed to process';
      } else if (status === 'skipped') {
        action = 'Skipped';
      }

      steps.push({
        stepId: `agent-${index}`,
        agent: transformAgentName(agent),
        status: status,
        action: action,
        reasoning: undefined
      });
    });

    // Add Strategy Agent as final step if not already present
    const hasStrategyAgent = steps.some(step =>
      step.agent.toLowerCase().includes('strategy')
    );

    if (!hasStrategyAgent && streamingResponse?.status !== 'completed') {
      // Check if Strategy Agent should be processing (use currentAgent/activeAgent)
      const normalizedCurrentAgent = (streamingResponse?.currentAgent || streamingResponse?.activeAgent) ? transformAgentName(streamingResponse?.currentAgent || streamingResponse?.activeAgent || '').toLowerCase().trim().replace(/\s+/g, ' ') : undefined;

      // Strategy Agent should only show as processing when ALL other agents have completed
      // This prevents it from showing as processing during initial planning phase
      const allOtherAgentsCompleted = executionPlanAgents.length > 0 &&
        executionPlanAgents.every(planAgent => {
          const normalizedPlanAgent = transformAgentName(planAgent).toLowerCase().trim().replace(/\s+/g, ' ');
          return steps.some(step =>
            step.agent.toLowerCase().trim().replace(/\s+/g, ' ') === normalizedPlanAgent &&
            step.status === 'completed'
          );
        });

      const isStrategyProcessing = normalizedCurrentAgent?.includes('strategy') &&
        streamingResponse?.status === 'processing' &&
        allOtherAgentsCompleted;

      steps.push({
        stepId: 'strategy-final',
        agent: 'Strategy Agent',
        status: isStrategyProcessing ? 'processing' : 'pending',
        action: isStrategyProcessing ? (streamingResponse?.currentStep || 'Synthesizing results...') : '',
        reasoning: undefined
      });
    }

    return steps;
  }, [executionPlanAgents, streamingResponse?.steps, streamingResponse?.status, streamingResponse?.currentAgent, streamingResponse?.activeAgent, streamingResponse?.currentStep, completedAgents, delayedCurrentAgent]);

  const handleSendMessage = () => {
    if (!currentMessage.trim()) return;

    // Check if the last message is an execution plan
    const lastMessage = messages[messages.length - 1];
    const isWaitingForApproval = lastMessage?.metadata?.type === 'execution_plan';

    if (isStreaming && !isWaitingForApproval) return;

    onSendMessage(currentMessage, attachedFiles, undefined);
    setCurrentMessage('');
  };


  // Render animated dots for processing
  const renderProcessingDots = () => {
    console.log('[ChatPanel] renderProcessingDots called:', {
      isStreaming,
      isChatLoading,
      hasStreamingResponse: !!streamingResponse,
      streamingResponse
    });

    // For follow-up queries (isChatLoading), show simple loading dots without text
    if (isChatLoading) {
      return (
        <div className="message ai-message">
          <div className="message-content">
            <div className="loading-dots-container">
              {/* No text for follow-up queries - just show loading dots */}
              <div className="loading-dots">
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
              </div>
            </div>
          </div>
          <div className="message-timestamp">
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      );
    }

    // For initial queries with streaming (isStreaming), show individual agent panels
    if (isStreaming && streamingResponse && streamingResponse.agentStatuses && streamingResponse.agentStatuses.length > 0) {
      // Filter out Strategy Agent from display (it's just for orchestration)
      const specialistAgents = streamingResponse.agentStatuses.filter(
        agent => !agent.agentName.toLowerCase().includes('strategy')
      );

      // Count completed specialist agents
      const completedCount = specialistAgents.filter(agent => agent.status === 'completed').length;
      const totalCount = specialistAgents.length;

      return (
        <div className="message ai-message">
          <div className="message-content">
            <div className="agent-progress-container">
              <h4 style={{ marginBottom: '16px', fontSize: '14px', fontWeight: 600 }}>
                Agent Progress ({completedCount}/{totalCount} completed)
              </h4>
              {specialistAgents.map((agent) => (
                <div key={agent.agentId} className="agent-panel" style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: agent.status === 'completed' ? '#f0f9ff' : '#ffffff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600 }}>{agent.agentName}</span>
                      {agent.status === 'processing' && (
                        <div className="loading-dots" style={{ display: 'inline-flex', gap: '4px' }}>
                          <div className="loading-dot" style={{ width: '6px', height: '6px' }}></div>
                          <div className="loading-dot" style={{ width: '6px', height: '6px' }}></div>
                          <div className="loading-dot" style={{ width: '6px', height: '6px' }}></div>
                        </div>
                      )}
                      {agent.status === 'completed' && (
                        <span style={{ color: '#10b981', fontSize: '12px' }}>✓ Completed</span>
                      )}
                    </div>
                  </div>
                  {agent.currentStep && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {agent.currentStep}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="message-timestamp">
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <style>{`
            @keyframes bounce {
              0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
              40% { transform: scale(1.2); opacity: 1; }
            }
          `}</style>
        </div>
      );
    }

    // Fallback: show simple loading
    const fallbackLoadingText = isAgentMode ? 'Initializing agents...' : 'Processing...';
    return (
      <div className="message ai-message">
        <div className="message-content">
          <div className="loading-dots-container">
            <p className="loading-text">{fallbackLoadingText}</p>
            <div className="loading-dots">
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
            </div>
          </div>
        </div>
        <div className="message-timestamp">
          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    );
  };

  // Render orchestration (streaming response only, no completion message)
  const renderOrchestration = () => {
    return (
      <>
        {renderStreamingResponse()}
      </>
    );
  };

  // Render streaming response (orchestration)
  const renderStreamingResponse = () => {
    // Return null if not streaming and no response
    if (!streamingResponse) return null;

    // Only show orchestration after user has actually approved the execution plan
    // But always show it if status is completed (even after streaming ends)
    // Keep showing orchestration for follow-up queries as well
    if (!hasUserApproved && streamingResponse.status !== 'completed') {
      return null;
    }

    return (
      <div className="message ai-message streaming-message">
        <div className="message-content">
          <div className="orchestration-section">
            <div
              className="orchestration-header"
              onClick={() => setIsOrchestrationExpanded(!isOrchestrationExpanded)}
            >
              <svg
                className={`orchestration-header-icon ${isOrchestrationExpanded ? '' : 'collapsed'}`}
                xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M11.4697 6.96967C11.7626 6.67678 12.2374 6.67678 12.5303 6.96967L20.0303 14.4697C20.3232 14.7626 20.3232 15.2374 20.0303 15.5303C19.7374 15.8232 19.2626 15.8232 18.9697 15.5303L12 8.56066L5.03033 15.5303C4.73744 15.8232 4.26256 15.8232 3.96967 15.5303C3.67678 15.2374 3.67678 14.7626 3.96967 14.4697L11.4697 6.96967Z" fill="#222325" />
              </svg>
              <h3 className="orchestration-header-title">
                {isStreaming ? 'Processing' : 'Orchestration Complete'}
              </h3>
            </div>

            {isOrchestrationExpanded && (orchestrationSteps.length > 0 || executionPlanAgents.length > 0) && (
              <>
                <div className="orchestration-steps">
                  {/* Strategy Intelligence Director - Removed to match final design */}

                  {/* Dynamic steps from prepopulated orchestrationSteps */}
                  {orchestrationSteps.map((step, index) => {
                    // Determine the visual state based on step status
                    const getStepStatusClass = () => {
                      switch (step.status) {
                        case 'completed': return 'completed';
                        case 'processing': return 'processing';
                        case 'failed': return 'failed';
                        case 'skipped': return 'skipped';
                        case 'pending':
                        default: return 'pending';
                      }
                    };

                    const statusClass = getStepStatusClass();

                    const isLastItem = index === orchestrationSteps.length - 1;

                    return (
                      <div key={step.stepId} className={`orchestration-item ${isLastItem ? 'orchestration-item-last' : ''}`}>
                        <div className="orchestration-item-content">
                          <div className="orchestration-avatar-wrapper">
                            <div className={`orchestration-avatar ${statusClass}`}>
                              {step.status === 'completed' ? (
                                <svg className="orchestration-success-icon" viewBox="0 0 16 16" fill="none">
                                  <path d="M13.5 4.5L6 12L2.5 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              ) : step.status === 'processing' ? (
                                <div className="orchestration-status-indicator"></div>
                              ) : step.status === 'failed' ? (
                                <svg className="orchestration-error-icon" viewBox="0 0 16 16" fill="none">
                                  <path d="M4 4L12 12M12 4L4 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              ) : step.status === 'skipped' ? (
                                <svg className="orchestration-skip-icon" viewBox="0 0 16 16" fill="none">
                                  <path d="M4 8H12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                              ) : (
                                <div className="orchestration-pending-indicator">
                                  <svg viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="8" r="8" stroke="currentColor" strokeWidth="1" opacity="0.3" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="orchestration-content">
                            <div className={`orchestration-title ${step.status === 'pending' ? 'orchestration-pending' : ''}`}>
                              {step.agent}
                            </div>
                            <div className={`orchestration-description ${step.status === 'pending' ? 'orchestration-pending' : ''}`}>
                              {step.status === 'processing' ?
                                (step.action || 'Gathering insights...') :
                                (step.action || step.reasoning || '')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Final Report - Show when completed or failed regardless of streaming status */}
                  {((streamingResponse.status === 'completed' || streamingResponse.status === 'failed') || (!isStreaming && streamingResponse.done)) && (
                    <div className="orchestration-item orchestration-item-final">
                      <div className="orchestration-item-content">
                        <div className="orchestration-avatar-wrapper">
                          <div className={`orchestration-avatar ${streamingResponse.status === 'failed' ? 'failed' : 'completed'}`}>
                            {streamingResponse.status === 'failed' ? (
                              <svg className="orchestration-error-icon" viewBox="0 0 16 16" fill="none">
                                <path d="M4 4L12 12M12 4L4 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <svg className="orchestration-success-icon" viewBox="0 0 16 16" fill="none">
                                <path d="M13.5 4.5L6 12L2.5 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="orchestration-content">
                          <div className="orchestration-title">
                            Finalise Results
                          </div>
                          <div className="orchestration-description">
                            Strategy Validation Report <span className="orchestration-report-status" style={streamingResponse.status === 'failed' ? { color: 'red' } : {}}>{streamingResponse.status === 'failed' ? 'Failed' : 'Complete'}</span>
                            <br />
                            <span className="orchestration-report-output">
                              Report Output: {projectName || (streamingResponse.result ? 'Available' : 'Processing...')}
                            </span>
                            <span className="orchestration-completion-details">
                              Completed: {streamingResponse.endTime ? new Date(streamingResponse.endTime).toLocaleString() : new Date().toLocaleString()}
                              <br />
                              Duration: {(() => {
                                // Get duration in milliseconds
                                let durationMs = 0;

                                // Use execution_time_ms if available
                                if (streamingResponse.execution_time_ms) {
                                  durationMs = streamingResponse.execution_time_ms;
                                }
                                // Calculate from startTime and endTime if available
                                else if (streamingResponse.startTime) {
                                  const startTime = new Date(streamingResponse.startTime).getTime();
                                  // Use endTime from response if available, otherwise use current time
                                  const endTime = streamingResponse.endTime
                                    ? new Date(streamingResponse.endTime).getTime()
                                    : Date.now();
                                  durationMs = endTime - startTime;
                                }

                                // Format the duration based on simple thresholds
                                if (durationMs > 0) {
                                  const totalSeconds = Math.floor(durationMs / 1000);

                                  // More than 60 minutes (1 hour) - show hours and minutes
                                  if (totalSeconds >= 3600) {
                                    const hours = Math.floor(totalSeconds / 3600);
                                    const minutes = Math.floor((totalSeconds % 3600) / 60);
                                    return `${hours}h ${minutes}m`;
                                  }
                                  // Between 60 seconds and 60 minutes - show only minutes
                                  else if (totalSeconds >= 60) {
                                    const minutes = Math.round(totalSeconds / 60);
                                    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
                                  }
                                  // Less than 60 seconds - show only seconds
                                  else {
                                    return `${totalSeconds} second${totalSeconds !== 1 ? 's' : ''}`;
                                  }
                                }
                                // Fallback to progress-based estimate
                                return `${streamingResponse.progress ? Math.round(streamingResponse.progress * 0.3) : 0} seconds`;
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="orchestration-footer">
                  <img
                    src="/images/k2-columbus.svg"
                    alt="K2 by columbus"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="message-timestamp">
          {streamingResponse.endTime ? new Date(streamingResponse.endTime).toLocaleString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    );
  };

  return (
    <div className={`chat-panel ${className || ''}`}>
      <div className="chat-messages" ref={chatMessagesRef} onScroll={handleScroll}>
        <div className="messages-container">
          {(() => {
            const shownExecutionPlanIds = new Set<string>();

            // Find the LAST message with is_result metadata to avoid duplicate orchestration rendering
            let lastResultMessageIndex = -1;
            for (let i = sortedMessages.length - 1; i >= 0; i--) {
              if (sortedMessages[i].metadata?.is_result) {
                lastResultMessageIndex = i;
                break;
              }
            }

            return sortedMessages.map((message, messageIndex) => {
              // Check if this is an execution plan message
              if (message.metadata?.type === 'execution_plan' && message.metadata.executionPlan) {
                // Use queryId from metadata, or fallback to message.id
                const executionPlanId = message.metadata.queryId || message.id;

                // Skip if we've already shown an execution plan with this ID
                if (shownExecutionPlanIds.has(executionPlanId)) {
                  return null;
                }
                shownExecutionPlanIds.add(executionPlanId);

                // Check if this is the last unique execution plan message
                // We need to find the last execution plan that has a unique ID
                let lastExecutionPlanIndex = -1;
                const seenPlanIds = new Set<string>();
                for (let i = sortedMessages.length - 1; i >= 0; i--) {
                  if (sortedMessages[i].metadata?.type === 'execution_plan') {
                    const planId = sortedMessages[i].metadata?.queryId || sortedMessages[i].id;
                    // Only consider this as the last if we haven't seen this ID before
                    if (!seenPlanIds.has(planId)) {
                      lastExecutionPlanIndex = i;
                      break;
                    }
                    seenPlanIds.add(planId);
                  }
                }
                const isLastMessage = messageIndex === lastExecutionPlanIndex;
                const executionPlan = message.metadata.executionPlan;
                return (
                  <React.Fragment key={message.id}>
                    <div className={`message ai-message`}>
                      <div className="message-content">
                        <div className="execution-plan-section">
                          <h4 className="execution-plan-title">
                            Execution Plan
                          </h4>

                          {/* Agents List */}
                          {executionPlan.agents && executionPlan.agents.length > 0 && (
                            <div className="execution-plan-agents">
                              <h5 className="execution-plan-agents-title">
                                Your query has been processed. The following specialised agents will be engaged:
                              </h5>
                              <ul className="execution-plan-agents-list">
                                <li className="execution-plan-agent-item">
                                  Strategy Agent
                                </li>
                                {executionPlan.agents.map((agent: any, index: number) => {
                                  // Handle both string and object formats
                                  const agentName = typeof agent === 'string'
                                    ? agent
                                    : (agent?.agentName || 'Unknown Agent');
                                  return (
                                    <li key={index} className="execution-plan-agent-item">
                                      {transformAgentName(agentName)}
                                    </li>
                                  );
                                })}
                              </ul>
                              <br />
                              <h5 className="execution-plan-agents-title">
                                They will collaborate to produce a comprehensive analysis covering:
                              </h5>
                              <ul className="execution-plan-agents-list">
                                {!projectName?.toLowerCase().includes('strategic positioning') && executionPlan.summary && Array.isArray(executionPlan.summary) ? (
                                  executionPlan.summary.map((item: string, index: number) => {
                                    // Parse the summary item to extract title and description
                                    // Handle multiple separators: "–" (en dash), "-" (hyphen), or ":"
                                    let title = item;
                                    let description = '';

                                    // Try different separators
                                    if (item.includes('–')) {
                                      const parts = item.split('–');
                                      title = parts[0]?.trim() || item;
                                      description = parts.slice(1).join('–').trim();
                                    } else if (item.includes(':')) {
                                      const parts = item.split(':');
                                      title = parts[0]?.trim() || item;
                                      description = parts.slice(1).join(':').trim();
                                    } else if (item.includes('-')) {
                                      const parts = item.split('-');
                                      title = parts[0]?.trim() || item;
                                      description = parts.slice(1).join('-').trim();
                                    }

                                    return (
                                      <li key={index} className="execution-plan-agent-item">
                                        <strong>{title}</strong>
                                        {description && ` – ${description}`}
                                      </li>
                                    );
                                  })
                                ) : (
                                  // Gitex Fallback to default items if summary is not available
                                  <>
                                    <li className="execution-plan-agent-item">
                                      <strong>Market</strong> — size & growth, demand drivers, barriers, and priority segments
                                    </li>
                                    <li className="execution-plan-agent-item">
                                      <strong>Competitive Landscape</strong> — share & positioning, wedge plays, differentiators, and whitespace vs incumbents/fintechs
                                    </li>
                                    <li className="execution-plan-agent-item">
                                      <strong>Finance</strong> — revenue pools, fee/NII mix, unit economics, CTI trajectory, and risk/return (RoRWA) levers
                                    </li>
                                    <li className="execution-plan-agent-item">
                                      <strong>Regulatory</strong> — licensing pathways, consumer duty & conduct, data/privacy, and instant-payment/open-banking implications
                                    </li>

                                  </>
                                )}</ul>
                            </div>
                          )}
                          <br />
                          <div>
                            <p className="execution-plan-question">
                              Please indicate if you prefer balanced coverage across all dimensions, or if specific areas should be prioritised for deeper analysis.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="message-timestamp">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    {/* Show Proceed button only if this execution plan is the last message */}
                    {isLastMessage && streamingResponse?.status === 'pending_approval' && streamingResponse?.executionPlan && (
                      <div key={`${message.id}-proceed`} className={`message ai-message`}>
                        <div
                          className={`message-content action-message ${isProceedClicked ? 'disabled' : ''}`}
                          onClick={() => {
                            // Prevent double-clicking
                            if (isProceedClicked) return;

                            setIsProceedClicked(true);
                            // Send Proceed with preserved attachments
                            onSendMessage('Proceed', [], {
                              source: 'approval_button',
                              queryId: message.metadata?.queryId || executionPlanId,
                              timestamp: new Date().toISOString(),
                              is_result: true,
                            });
                          }}
                          style={{
                            pointerEvents: (isProceedClicked) ? 'none' : 'auto',
                            opacity: isProceedClicked ? 0.5 : 1
                          }}
                        >
                          Proceed
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              }

              // Handle file messages with special rendering
              if (message.role === 'file') {
                return (
                  <div key={message.id} className="message file-message">
                    <div className="file-message-list">
                      {message.metadata?.files?.map((file, index) => {
                        const IconComponent = getFileIcon(file.name || '');
                        return (
                          <div key={index} className="file-item">
                            <div className="file-item-info">
                              <IconComponent className="file-item-icon" size={20} />
                              <div className="file-item-details">
                                <span className="file-item-name">{file.name || 'Unnamed File'}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Regular message rendering (without inline attachments)
              return (
                <React.Fragment key={message.id}>
                  <div className={`message ${message.role === 'user' ? 'user-message' : 'ai-message'} ${message.metadata?.isError ? 'error-message' : ''}`}>
                    <div className="message-content">
                      {/* Show retry button for error messages inline with the text */}
                      {message.metadata?.isError && message.metadata?.canRetry ? (
                        <div className="error-message-with-retry">
                          <span className="error-message-text">{message.content}</span>
                          <button
                            onClick={() => {
                              if (message.metadata?.originalQuery) {
                                onSendMessage(message.metadata.originalQuery, []);
                              }
                            }}
                            className="error-retry-button"
                          >
                            Retry
                          </button>
                        </div>
                      ) : (
                        formatMessageContent(message.content)
                      )}
                    </div>
                    <div className="message-status">
                      {/* Show upload spinner for user messages that are currently uploading files */}
                      {message.role === 'user' && isUploadingFiles && uploadingMessageId === message.id && (
                        <>
                          <SpinningLoader size="small" />
                          <span className="status-text">Uploading files... ({uploadProgress}%)</span>
                        </>
                      )}
                      <div className="message-timestamp">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {/* Show success icon for user messages */}
                      {message.role === 'user' && (
                        <DoubleCheckIcon
                          className={`message-success-icon ${(!isUploadingFiles) ? 'sent' : ''}`}
                          size={12}
                        />
                      )}
                    </div>
                  </div>
                  {/* Show orchestration ONLY under the LAST message with is_result metadata to avoid duplication */}
                  {message.metadata?.is_result && messageIndex === lastResultMessageIndex && renderOrchestration()}
                </React.Fragment>
              );
            });
          })()}

          {/* Show processing dots after the last message */}
          {(() => {
            // Show processing dots if we're still streaming and either processing or pending approval (before plan is shown)
            const lastMessage = sortedMessages[sortedMessages.length - 1];
            const lastMessageIsExecutionPlan = lastMessage?.metadata?.type === 'execution_plan';

            console.log('[ChatPanel] Checking if should show processing dots:', {
              isStreaming,
              hasStreamingResponse: !!streamingResponse,
              status: streamingResponse?.status,
              hasUserApproved,
              lastMessageIsExecutionPlan,
              isChatLoading,
              shouldShow: (isStreaming && streamingResponse && (streamingResponse.status === 'processing' || streamingResponse.status === 'pending_approval') && !hasUserApproved && !lastMessageIsExecutionPlan) || isChatLoading
            });

            if ((isStreaming && streamingResponse && (streamingResponse.status === 'processing' || streamingResponse.status === 'pending_approval') && !hasUserApproved && !lastMessageIsExecutionPlan) || isChatLoading) {
              return renderProcessingDots();
            }
            return null;
          })()}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-input-area">
        <SendMessageInput
          value={currentMessage}
          onChange={setCurrentMessage}
          onSubmit={handleSendMessage}
          attachedFiles={attachedFiles}
          onFileSelect={onFileSelect}
          onFileRemove={onFileRemove}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onPaste={onPaste}
          isDragOver={isDragOver}
          variant="chat"
          disabled={(() => {
            const lastMessage = sortedMessages[sortedMessages.length - 1];
            const isWaitingForApproval = lastMessage?.metadata?.type === 'execution_plan';
            return isStreaming && !isWaitingForApproval;
          })()}
        />
      </div>
    </div>
  );
};

export default ChatPanel;